import { useEffect, useRef, useState } from 'react';
import { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import type { ListPaginated } from '@/types/api';

interface UseInfiniteScrollFetchProps<T> {
  infiniteQuery: UseInfiniteQueryResult<InfiniteData<ListPaginated<T>>, Error>;
  rootMargin?: string;
  threshold?: number;
}

/**
 * Custom hook for implementing infinite scroll with React Query
 * Uses Intersection Observer API to detect when user scrolls near bottom
 * Automatically triggers fetching of next page when sentinel element is visible
 */
export function useInfiniteScrollFetch<T>({
  infiniteQuery,
  rootMargin = '100px',
  threshold = 0.1,
}: UseInfiniteScrollFetchProps<T>) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = infiniteQuery;

  const [isFetching, setIsFetching] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];

        if (
          target.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetching
        ) {
          setIsFetching(true);
          fetchNextPage().finally(() => {
            setIsFetching(false);
          });
        }
      },
      {
        root: null,
        rootMargin,
        threshold,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    rootMargin,
    threshold,
  ]);

  // Flatten all pages into single array
  const allItems = data?.pages.flatMap(page => page.data) || [];

  // Calculate total count from last page
  const totalCount = data?.pages[data.pages.length - 1]?.pagination?.count || 0;

  return {
    // Data
    items: allItems,
    totalCount,

    // Loading states
    isLoading,
    isFetchingNextPage: isFetchingNextPage || isFetching,
    hasNextPage,

    // Error state
    error,

    // Ref for sentinel element
    sentinelRef,

    // Manual controls
    fetchNextPage: () => {
      if (hasNextPage && !isFetchingNextPage && !isFetching) {
        setIsFetching(true);
        fetchNextPage().finally(() => {
          setIsFetching(false);
        });
      }
    },
  };
}

export type UseInfiniteScrollFetchReturn<T> = ReturnType<
  typeof useInfiniteScrollFetch<T>
>;
